// GCS Client for frontend - communicates with Edge Functions
import { supabase } from './supabase-client';
import { publicAnonKey as importedAnonKey } from '../../utils/supabase/info';

// Get Anon Key safely
const publicAnonKey = importedAnonKey || import.meta.env.VITE_SUPABASE_ANON_KEY;

const EDGE_FUNCTION_URL = 'https://uyavxanmcwfelwqwsesr.supabase.co/functions/v1';
const BASE_PATH = '/server';

/**
 * Get standard auth headers for Edge Function calls
 * Supabase Edge Functions require both Authorization AND apikey headers
 */
async function getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };

    // Authorization header (Bearer token from session or anon key)
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    } else if (publicAnonKey) {
        headers['Authorization'] = `Bearer ${publicAnonKey}`;
    }

    // API Key header (Always required by Supabase Gateway)
    if (publicAnonKey) {
        headers['apikey'] = publicAnonKey;
    }

    return headers;
}

interface UploadUrlResponse {
    uploadUrl: string;
    gcsPath: string;
    publicUrl: string;
}

interface UploadResponse {
    success: boolean;
    gcsPath: string;
    publicUrl: string;
    fileName: string;
    fileSize: number;
}

// Get upload URL for direct upload to GCS
export async function getUploadUrl(
    bucket: 'wysko-originals' | 'wysko-generated' | 'wysko-assets',
    fileName: string,
    contentType: string,
    userId?: string,
    propertyId?: string
): Promise<UploadUrlResponse> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${EDGE_FUNCTION_URL}${BASE_PATH}/gcs/upload-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            bucket,
            fileName,
            contentType,
            userId,
            propertyId,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get upload URL');
    }

    return response.json();
}

// Upload file directly to GCS using resumable upload URL
export async function uploadToGCS(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const progress = (e.loaded / e.total) * 100;
                onProgress(progress);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Upload failed'));
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
    });
}

// Upload file via Edge Function (for smaller files, simpler)
export async function uploadViaEdgeFunction(
    file: File,
    bucket: 'wysko-originals' | 'wysko-generated' | 'wysko-assets' = 'wysko-originals',
    userId?: string,
    propertyId?: string,
    customPath?: string
): Promise<UploadResponse> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucket);
    if (userId) formData.append('userId', userId);
    if (propertyId) formData.append('propertyId', propertyId);
    if (customPath) formData.append('customPath', customPath);

    const headers: HeadersInit = {};

    // Authorization header (Bearer token)
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // API Key header (Required by Supabase Gateway)
    if (publicAnonKey) {
        headers['apikey'] = publicAnonKey;
    } else {
        console.error('Missing Supabase Anon Key! Upload will likely fail.');
    }

    // Debug headers
    console.log('Upload headers:', { ...headers, Authorization: token ? 'Bearer [HIDDEN]' : 'None' });

    const response = await fetch(`${EDGE_FUNCTION_URL}${BASE_PATH}/gcs/upload`, {
        method: 'POST',
        headers,
        body: formData,
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
    }

    return response.json();
}

// Get download URL for private files
export async function getDownloadUrl(
    bucket: 'wysko-originals' | 'wysko-generated',
    gcsPath: string
): Promise<string> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${EDGE_FUNCTION_URL}${BASE_PATH}/gcs/download-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            bucket,
            gcsPath,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get download URL');
    }

    const data = await response.json();
    return data.downloadUrl;
}

// Get public URL (if bucket is public)
// Batch Sign URLs
export async function batchSignUrls(
    items: { bucket: string; path: string; }[]
): Promise<Record<string, string>> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${EDGE_FUNCTION_URL}${BASE_PATH}/gcs/sign-urls`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ items }),
    });

    if (!response.ok) {
        console.error('Batch sign failed:', response.status, response.statusText);
        return {};
    }

    const { data } = await response.json();
    return data;
}

// Get public URL is deprecated/removed in favor of Signed URLs
// Return placeholder or error if used
export function getPublicUrl(
    bucket: 'wysko-originals' | 'wysko-generated',
    gcsPath: string
): string {
    console.warn('getPublicUrl is deprecated. Use batchSignUrls instead.');
    return '';
}
